import { useEffect, useRef, useState, type FormEvent } from "react";
import { Building2, CheckCircle2, Eye, EyeOff, LoaderCircle, MailCheck, ShieldCheck, UserPlus } from "lucide-react";
import "./product-onboarding-visual.css";
import "./signup-consent.css";
import "./product-onboarding-r319.css";
import type { Session } from "../api.js";
import {
  productApi,
  type ProductApi,
  type ProductBusiness,
  type ProductSession,
  type ProductSignupPolicy,
  type ProductSignupLegalAcceptance
} from "../product-api.js";
import {
  isPreparationAbort,
  isWorkspaceNotReady,
  prepareBusinessWorkspace,
  throwIfPreparationAborted,
  workspacePreparationCopy,
  type WorkspacePreparation
} from "./business-runtime-preparation.js";

type AuthMode = "SIGN_IN" | "SIGN_UP" | "CONFIRM";

type SignupDraft = {
  email: string;
  password: string;
  displayName: string;
  businessName: string;
  timezone: string;
};

function policyPage(kind: "terms" | "privacy", source: string): string {
  return `/auth-policy.html?kind=${kind}&source=${encodeURIComponent(source)}`;
}

export function ProductOnboarding({
  onLogin,
  legacyLogin,
  initialBusinessSlug = "",
  initialAuthMode = "SIGN_IN",
  confirmationLinkToken,
  alreadySignedIn = false,
  onLeaveConfirmation,
  apiClient = productApi
}: {
  onLogin: (session: Session) => void;
  legacyLogin: (login: string, password: string) => Promise<Session>;
  loadWorkspaceSession: () => Promise<Session>;
  initialBusinessSlug?: string;
  initialAuthMode?: "SIGN_IN" | "SIGN_UP";
  confirmationLinkToken?: string;
  alreadySignedIn?: boolean;
  onLeaveConfirmation?: () => void;
  apiClient?: ProductApi;
}) {
  const [mode, setMode] = useState<AuthMode>(confirmationLinkToken === undefined ? initialAuthMode : "CONFIRM");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [businesses, setBusinesses] = useState<readonly ProductBusiness[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(confirmationLinkToken === "" ? "This confirmation link is invalid or expired." : "");
  const [status, setStatus] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof SignupDraft, string>>>({});
  const [retryAt, setRetryAt] = useState(0);
  const [retrySeconds, setRetrySeconds] = useState(0);
  const [signupPolicy, setSignupPolicy] = useState<ProductSignupPolicy | null>(null);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [policyRevision, setPolicyRevision] = useState(0);
  const [submittedAcceptance, setSubmittedAcceptance] = useState<ProductSignupLegalAcceptance | null>(null);
  const [signup, setSignup] = useState<SignupDraft>({
    email: "",
    password: "",
    displayName: "",
    businessName: "",
    timezone: browserTimezone()
  });
  const [confirmationToken, setConfirmationToken] = useState(confirmationLinkToken ?? "");
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [linkEntry, setLinkEntry] = useState(confirmationLinkToken !== undefined);
  const [confirmed, setConfirmed] = useState(false);
  const confirmationInFlight = useRef(false);
  // WHY [BOL-SELF-SERVE-WEB-001]: the "Preparing your workspace" wait must stop
  // polling when the form unmounts or the person switches to another tab.
  // Without this abort a closed sign-in screen keeps reading the server and
  // could still reload the page into a business the person left.
  const [preparing, setPreparing] = useState<WorkspacePreparation | null>(null);
  const preparationAbort = useRef<AbortController | null>(null);
  const previousTitle = useRef(document.title);
  useEffect(() => () => preparationAbort.current?.abort(), []);
  useEffect(() => {
    document.title = `${mode === "SIGN_UP" ? "Create account" : mode === "CONFIRM" ? "Confirm email" : "Sign in"} · Business Ops`;
    return () => { document.title = previousTitle.current; };
  }, [mode]);

  useEffect(() => {
    if (mode !== "SIGN_UP") return;
    let active = true;
    setSignupPolicy(null);
    setLegalAccepted(false);
    void apiClient.signupPolicy().then(policy => {
      if (active) setSignupPolicy(policy);
    }).catch(() => { if (active) setSignupPolicy({ available: false }); });
    return () => { active = false; };
  }, [mode, apiClient, policyRevision]);

  useEffect(() => {
    if (confirmationLinkToken === undefined) return;
    setConfirmationToken(confirmationLinkToken);
    setLinkEntry(true);
    setConfirmed(false);
    setError(confirmationLinkToken ? "" : "This confirmation link is invalid or expired.");
    setStatus("");
    setMode("CONFIRM");
  }, [confirmationLinkToken]);

  useEffect(() => {
    if (!retryAt) return;
    const update = () => setRetrySeconds(Math.max(0, Math.ceil((retryAt - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [retryAt]);

  const changeMode = (next: AuthMode) => {
    preparationAbort.current?.abort();
    preparationAbort.current = null;
    setPreparing(null);
    setMode(next);
    setLinkEntry(false);
    setConfirmationToken("");
    setConfirmationCode("");
    setConfirmed(false);
    setError("");
    setStatus("");
    setBusinesses([]);
    setFieldErrors({});
  };

  // WHY [BOL-CONFIRM-NORMAL-SIGNIN-001]: acceptance deliberately hides account
  // existence. Offer the same normal sign-in escape to everyone, before the code
  // field; preserve only the typed email, never a password, token or session.
  const signInFromConfirmation = () => {
    setLogin((confirmationEmail || signup.email).trim());
    changeMode("SIGN_IN");
  };

  const finishProductLogin = (businessSlug?: string, allowLegacyFallback = false) => {
    preparationAbort.current?.abort();
    const controller = new AbortController();
    preparationAbort.current = controller;
    return authenticateProductAccount({
      apiClient, login, password, businessSlug, allowLegacyFallback, legacyLogin, onLogin,
      onSelection: (choices) => {
        setBusinesses(choices);
        setStatus("Choose the business you want to open.");
      },
      reload: () => window.location.assign("/"),
      onPreparing: (progress) => {
        if (!controller.signal.aborted) setPreparing(progress);
      },
      signal: controller.signal
    });
  };

  const submitLogin = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setStatus("");
    try {
      // WHY [BOL-PRODUCT-LOGIN-COMPAT-001]: product accounts are email based,
      // while the accepted production desk still supports a short owner login.
      // Probe product auth only for an email and fall back only on literal
      // invalid credentials; never turn a business-selection or workspace error
      // into a different account login. Falsifier: a short owner can no longer
      // sign in, or a valid product account silently enters the legacy account.
      if (!looksLikeEmail(login)) {
        if (initialBusinessSlug) throw new Error("Enter the email address for the business account you want to open.");
        onLogin(await legacyLogin(login, password));
        return;
      }
      await finishProductLogin(initialBusinessSlug || undefined, !initialBusinessSlug);
    } catch (cause) {
      // A cancelled wait (unmount or tab switch) is not a sign-in failure.
      if (isPreparationAbort(cause)) return;
      setPreparing(null);
      setError(cause instanceof Error ? cause.message : "Login or password is incorrect.");
    } finally {
      setBusy(false);
    }
  };

  const chooseBusiness = async (business: ProductBusiness) => {
    setBusy(true);
    setError("");
    setStatus(`Opening ${business.name}.`);
    try {
      await finishProductLogin(business.slug);
    } catch (cause) {
      if (isPreparationAbort(cause)) return;
      setPreparing(null);
      setError(cause instanceof Error ? cause.message : "That business could not be opened.");
      setStatus("");
    } finally {
      setBusy(false);
    }
  };
  const preparationCopy = preparing ? workspacePreparationCopy(preparing) : null;

  const submitSignup = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || Date.now() < retryAt) return;
    if (!signupPolicy?.available || !legalAccepted) {
      setError(signupPolicy?.available ? "Read the Terms and Privacy Notice, then confirm your agreement." : "Signup documents are not available. You can still sign in.");
      return;
    }
    const form = event.currentTarget;
    // WHY: the old HTML pattern was invalid under Chrome's Unicode-v rules.
    // Invalid slugs reached the server, then repeated blind retries used the
    // signup allowance. Validate before any request and keep every error at
    // its field; the API remains the final authority on accepted input.
    const invalid: Partial<Record<keyof SignupDraft, string>> = {};
    for (const control of Array.from(form.elements)) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) continue;
      const field = control.name as keyof SignupDraft;
      if (field in signup && (!control.validity.valid || !control.value.trim())) invalid[field] = signupFieldMessage(field);
    }
    setFieldErrors(invalid);
    if (Object.keys(invalid).length) {
      setError("Fix the marked fields.");
      (form.elements.namedItem(Object.keys(invalid)[0]!) as HTMLElement | null)?.focus();
      return;
    }
    setBusy(true);
    setError("");
    setStatus("");
    try {
      const legalAcceptance: ProductSignupLegalAcceptance = {
        termsAccepted: true, privacyAcknowledged: true, releaseId: signupPolicy.releaseId,
        termsVersion: signupPolicy.terms.version, termsSha256: signupPolicy.terms.sha256,
        privacyVersion: signupPolicy.privacy.version, privacySha256: signupPolicy.privacy.sha256
      };
      // WHY [BOL-SIGNUP-HIDDEN-SLUG-894]: the API still needs an internal slug
      // prefix, but the person only names the business. Derive it at submission
      // from that name; the server binds identity to a fresh workspace UUID.
      const result = await apiClient.signup({ ...signup, businessSlug: businessSlugFromName(signup.businessName), legalAcceptance });
      setSubmittedAcceptance(legalAcceptance);
      setStatus(signupDeliveryMessage(result.deliveryAdapter));
      setConfirmationEmail(signup.email);
      setConfirmationCode("");
      setRetryAt(Date.now() + 60_000);
      setConfirmationToken("");
      setLinkEntry(false);
      setConfirmed(false);
      setMode("CONFIRM");
    } catch (cause) {
      const failure = (typeof cause === "object" && cause !== null ? cause : {}) as { code?: string; issues?: { path?: unknown[] }[]; retryAfterSeconds?: number };
      const fields: Partial<Record<keyof SignupDraft, string>> = {};
      if (failure.issues?.some(issue => issue.path?.[0] === "legalAcceptance")) {
        setLegalAccepted(false);
        setPolicyRevision(revision => revision + 1);
        setError("The signup documents changed. Read and accept the current versions.");
        return;
      }
      if (failure.code === "INVALID_INPUT" && Array.isArray(failure.issues)) {
        for (const issue of failure.issues) {
          const field = issue.path?.[0];
          if (field === "businessSlug") {
            fields.businessName = "Try another business name.";
            continue;
          }
          if (typeof field === "string" && Object.prototype.hasOwnProperty.call(signup, field)) fields[field as keyof SignupDraft] = signupFieldMessage(field as keyof SignupDraft);
        }
      }
      setFieldErrors(fields);
      if (Object.keys(fields).length) {
        setError("Fix the marked fields.");
        (form.elements.namedItem(Object.keys(fields)[0]!) as HTMLElement | null)?.focus();
      } else if (failure.retryAfterSeconds && failure.retryAfterSeconds > 0) {
        setRetrySeconds(failure.retryAfterSeconds);
        setRetryAt(Date.now() + failure.retryAfterSeconds * 1000);
        setError("Too many signup attempts. Your details are still here. Wait, then try again.");
      } else {
        setError(failure.code === "INVALID_INPUT" ? "We couldn’t accept these details. Check the form and try again." : cause instanceof Error ? cause.message : "The account could not be created.");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitConfirmation = async (event: FormEvent) => {
    event.preventDefault();
    if (confirmationInFlight.current || confirmed || (!confirmationToken.trim() && !/^[0-9]{6}$/.test(confirmationCode))) return;
    confirmationInFlight.current = true;
    setBusy(true);
    setError("");
    try {
      if (linkEntry || confirmationToken.trim()) await apiClient.confirmEmail(confirmationToken.trim());
      else await apiClient.confirmEmailCode(confirmationEmail, confirmationCode);
      setConfirmationToken("");
      setConfirmationCode("");
      setSignup((current) => ({ ...current, password: "" }));
      if (!alreadySignedIn && confirmationEmail) setLogin(confirmationEmail);
      setConfirmed(true);
      setStatus(alreadySignedIn
        ? "Email confirmed. Your current business session was not changed."
        : "Email confirmed. Sign in to open your business.");
      if (!alreadySignedIn) setMode("SIGN_IN");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That confirmation token is invalid or expired.");
    } finally {
      confirmationInFlight.current = false;
      setBusy(false);
    }
  };

  const resendConfirmation = async () => {
    if (busy || Date.now() < retryAt || !signup.password || confirmationEmail !== signup.email || !submittedAcceptance) return;
    setBusy(true);
    setError("");
    try {
      // The confirmation retry must carry the same generated internal prefix
      // as first signup; dropping it silently turns a valid retry into a 400.
      const result = await apiClient.signup({ ...signup, businessSlug: businessSlugFromName(signup.businessName), legalAcceptance: submittedAcceptance });
      setStatus(signupDeliveryMessage(result.deliveryAdapter));
      setConfirmationCode("");
      setRetryAt(Date.now() + 60_000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The request could not be completed.");
      setRetryAt(Date.now() + 60_000);
    } finally { setBusy(false); }
  };

  return (
    <main className={`login-page login-page--simple product-onboarding product-onboarding--${mode.toLowerCase()}`}>
      <section className="login-simple" aria-label="Business Ops account access">
        <div className="login-simple__brand"><div className="account-cube-stage"><img className="account-cube" src="/assets/ops-cube-logo-r72-512.png" width="512" height="512" alt="" aria-hidden="true" /></div><span><strong>Business Ops</strong><small>Your account</small></span></div>
        <div className="product-auth-card">
          {!alreadySignedIn ? <nav className="product-auth-tabs" aria-label="Account options">
            <button type="button" aria-pressed={mode === "SIGN_IN"} onClick={() => changeMode("SIGN_IN")}>Sign in</button>
            <button type="button" aria-pressed={mode === "SIGN_UP" || mode === "CONFIRM"} onClick={() => changeMode("SIGN_UP")}>Create account</button>
          </nav> : null}

          {mode === "SIGN_IN" && preparationCopy ? <section className="product-auth-form product-auth-preparing" aria-labelledby="workspace-preparing-title" aria-busy="true">
            <span className="login-form__shield"><LoaderCircle className="spin" size={23} aria-hidden="true" /></span>
            <p className="eyebrow">Workspace setup</p>
            <h1 id="workspace-preparing-title">Preparing your workspace</h1>
            <p className="product-auth-form__intro" role="status" aria-live="polite">{preparationCopy.message}</p>
            {preparing ? <WorkspacePreparationStages progress={preparing} /> : null}
            {preparationCopy.note ? <p className="form-status">{preparationCopy.note}</p> : null}
            <button className="quiet-review-action" type="button" onClick={() => changeMode("SIGN_IN")}>Back to sign in</button>
          </section> : null}

          {mode === "SIGN_IN" && !preparationCopy ? <form className="product-auth-form" onSubmit={(event) => void submitLogin(event)}>
            <span className="login-form__shield"><ShieldCheck size={23} aria-hidden="true" /></span>
            <p className="eyebrow">Private workspace</p>
            <h1>{initialBusinessSlug ? "Switch business" : "Sign in"}</h1>
            <p className="product-auth-form__intro">{initialBusinessSlug ? `Re-enter your details to open ${initialBusinessSlug}.` : "Sign in to your business."}</p>
            <label><span>Email or login</span><input type="text" autoComplete="username" required value={login} onChange={(event) => setLogin(event.target.value)} /></label>
            <label><span>Password</span><span className="password-field"><input type={showPassword ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} /><button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></span></label>
            {businesses.length > 0 ? <fieldset className="business-choice"><legend>Choose a business</legend>{businesses.map((business) => <button key={business.id} type="button" disabled={busy} onClick={() => void chooseBusiness(business)}><span className="business-choice__icon"><Building2 size={18} aria-hidden="true" /></span><span><strong>{business.name}</strong><small>{business.slug} · {roleLabel(business.role)} · {business.timezone}</small></span></button>)}</fieldset> : null}
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            {status ? <p className="form-status" role="status">{status}</p> : null}
            <button className="primary-button login-submit" type="submit" disabled={busy || businesses.length > 0}>{busy ? <><LoaderCircle className="spin" size={18} />Signing in…</> : businesses.length > 0 ? "Choose a business above" : "Sign in"}</button>
          </form> : null}

          {mode === "SIGN_UP" ? <form className="product-auth-form" noValidate onInput={(event) => {
            const control = event.target;
            if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
            const field = control.name as keyof SignupDraft;
            if (!control.validity.valid || !control.value.trim()) return;
            // Keep unrelated server/rate-limit errors visible while editing.
            if (fieldErrors[field] && Object.keys(fieldErrors).length === 1 && error === "Fix the marked fields.") setError("");
            setFieldErrors((current) => { const next = { ...current }; delete next[field]; return next; });
          }} onSubmit={(event) => void submitSignup(event)}>
            <span className="login-form__shield"><UserPlus size={23} aria-hidden="true" /></span>
            <p className="eyebrow">New account</p>
            <h1>Create your account</h1>
            <p className="product-auth-form__intro">Your account can own more than one business. Each business keeps its people, connections, and work separate.</p>
            <div className="product-auth-grid">
              <label><span>Your name</span><input name="displayName" id="signup-displayName" aria-invalid={Boolean(fieldErrors.displayName)} type="text" autoComplete="name" required maxLength={160} aria-describedby={fieldErrors.displayName ? "signup-displayName-error" : undefined} value={signup.displayName} onChange={(event) => setSignup((current) => ({ ...current, displayName: event.target.value }))} />{fieldErrors.displayName ? <small className="signup-field-error" id="signup-displayName-error">{fieldErrors.displayName}</small> : null}</label>
              <label><span>Email</span><input name="email" id="signup-email" aria-invalid={Boolean(fieldErrors.email)} type="email" autoComplete="email" required maxLength={320} aria-describedby={fieldErrors.email ? "signup-email-error" : undefined} value={signup.email} onChange={(event) => setSignup((current) => ({ ...current, email: event.target.value }))} />{fieldErrors.email ? <small className="signup-field-error" id="signup-email-error">{fieldErrors.email}</small> : null}</label>
              <label><span>Business name</span><input name="businessName" id="signup-businessName" aria-invalid={Boolean(fieldErrors.businessName)} type="text" autoComplete="organization" required maxLength={160} aria-describedby={fieldErrors.businessName ? "signup-businessName-error" : undefined} value={signup.businessName} onChange={(event) => {
                const businessName = event.target.value;
                setSignup((current) => ({ ...current, businessName }));
              }} />{fieldErrors.businessName ? <small className="signup-field-error" id="signup-businessName-error">{fieldErrors.businessName}</small> : null}</label>
              <label><span>Timezone</span><select name="timezone" id="signup-timezone" aria-invalid={Boolean(fieldErrors.timezone)} required aria-describedby={fieldErrors.timezone ? "signup-timezone-help signup-timezone-error" : "signup-timezone-help"} value={signup.timezone} onChange={(event) => setSignup((current) => ({ ...current, timezone: event.target.value }))}>{availableTimeZones(signup.timezone).map((timeZone) => <option key={timeZone} value={timeZone}>{timeZoneLabel(timeZone)}</option>)}</select><small id="signup-timezone-help">Selected automatically from your device. You can change it.</small>{fieldErrors.timezone ? <small className="signup-field-error" id="signup-timezone-error">{fieldErrors.timezone}</small> : null}</label>
              <label><span>Password</span><input name="password" id="signup-password" aria-invalid={Boolean(fieldErrors.password)} type="password" autoComplete="new-password" required minLength={15} maxLength={256} aria-describedby={fieldErrors.password ? "signup-password-help signup-password-error" : "signup-password-help"} value={signup.password} onChange={(event) => setSignup((current) => ({ ...current, password: event.target.value }))} /><small id="signup-password-help">Use at least 15 characters.</small>{fieldErrors.password ? <small className="signup-field-error" id="signup-password-error">{fieldErrors.password}</small> : null}</label>
            </div>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            {signupPolicy?.available ? <fieldset className="product-signup-consent" aria-label="WhatsApp authorization and policies">
              {/* WHY [BOL-AUTH-CONSENT-R319]: one readable unchecked decision
                  preserves the same version/hash assent while keeping policy
                  documents directly reachable without visible version noise. */}
              <div className="product-signup-consent__row">
                <input id="signup-legal-acceptance" type="checkbox" name="legalAcceptance" required checked={legalAccepted} onChange={(event) => setLegalAccepted(event.target.checked)} />
                <div className="product-signup-consent__copy">
                  <label htmlFor="signup-legal-acceptance">I authorize Business Ops to connect to WhatsApp and process my messages.</label>
                  <span>I agree to the <a href={policyPage("terms", signupPolicy.terms.url)} target="_blank" rel="noopener noreferrer">Terms</a> and acknowledge the <a href={policyPage("privacy", signupPolicy.privacy.url)} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</span>
                </div>
              </div>
            </fieldset> : <p className="form-status" role="status">{signupPolicy === null ? "Loading signup documents…" : "Account creation is unavailable until the Terms and Privacy Notice are published. You can still sign in."}</p>}
            <button className="primary-button login-submit" type="submit" disabled={busy || retrySeconds > 0 || !signupPolicy?.available || !legalAccepted}>{busy ? <><LoaderCircle className="spin" size={18} />Creating account…</> : retrySeconds > 0 ? `Try again in ${retrySeconds}s` : "Create account"}</button>
            <p>Already created an account? <button className="quiet-review-action" type="button" onClick={() => changeMode("CONFIRM")}>Confirm your email</button></p>
          </form> : null}

          {mode === "CONFIRM" ? <form className="product-auth-form" onSubmit={(event) => void submitConfirmation(event)}>
            <span className="login-form__shield"><MailCheck size={23} aria-hidden="true" /></span>
            <p className="eyebrow">Email confirmation</p>
            <h1>Confirm your email</h1>
            <p className="product-auth-form__intro">{linkEntry ? "Confirm the email address linked to this one-use link." : "For a new account, enter the code from your email. The code expires in 15 minutes."}</p>
            {!alreadySignedIn ? <button className="quiet-review-action" type="button" disabled={busy} onClick={signInFromConfirmation}>Already have an account? Sign in instead.</button> : null}
            {!linkEntry ? <>
              <label><span>Email</span><input type="email" autoComplete="email" required={!confirmationToken.trim()} value={confirmationEmail} onChange={(event) => setConfirmationEmail(event.target.value)} /></label>
              <label><span>Six-digit confirmation code</span><input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} autoComplete="one-time-code" required={!confirmationToken.trim()} value={confirmationCode} onChange={(event) => { setConfirmationCode(event.target.value.replace(/[^0-9]/g, "").slice(0, 6)); setConfirmationToken(""); }} /></label>
              <details><summary>Have an administrator's confirmation token?</summary><label><span>Confirmation token</span><textarea rows={3} minLength={32} maxLength={512} value={confirmationToken} onChange={(event) => { setConfirmationToken(event.target.value); setConfirmationCode(""); }} /></label></details>
            </> : null}
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            {status ? <p className="form-status" role="status"><CheckCircle2 size={17} aria-hidden="true" />{status}</p> : null}
            {!confirmed ? <button className="primary-button login-submit" type="submit" disabled={busy || (!confirmationToken.trim() && !/^[0-9]{6}$/.test(confirmationCode))}>{busy ? <><LoaderCircle className="spin" size={18} />Confirming…</> : "Confirm email"}</button> : null}
            {!linkEntry && !confirmed ? signup.password && confirmationEmail === signup.email
              ? <button className="quiet-review-action" type="button" disabled={busy || retrySeconds > 0} onClick={() => void resendConfirmation()}>{retrySeconds > 0 ? `Request another email in ${retrySeconds}s` : "Send another confirmation email"}</button>
              : <button className="quiet-review-action" type="button" onClick={() => changeMode("SIGN_UP")}>Request another email with your signup details</button> : null}
            {alreadySignedIn && onLeaveConfirmation ? <button className="quiet-review-action" type="button" onClick={onLeaveConfirmation}>Return to your business</button> : null}
          </form> : null}
        </div>
        <small className="login-fineprint">Private workspace · Activity is recorded for accountability</small>
      </section>
    </main>
  );
}

// WHY [BOL-SETUP-TRUTHFUL-STAGES-863]: a first workspace can wait several
// minutes. Never advance decorative progress on time: QUEUED can also mean a
// request has not reached the server. Only PROVISIONED/RUNNING advances to
// STARTING, and a successful same-business selection advances to OPENING.
// Finer provisioning steps are not exposed by this API and must not be invented.
export function WorkspacePreparationStages({ progress }: { progress: WorkspacePreparation }) {
  const current = progress.phase === "OPENING" ? 2 : progress.phase === "STARTING" ? 1 : 0;
  const stages = ["Request workspace", "Start workspace", "Open workspace"];
  return <ol className="workspace-preparation-stages" aria-label="Workspace setup stages">
    {stages.map((label, index) => <li key={label} data-state={index < current ? "complete" : index === current ? "current" : "pending"} aria-current={index === current ? "step" : undefined}>
      <span className="workspace-preparation-stages__mark" aria-hidden="true">{index < current ? <CheckCircle2 size={18} /> : index === current ? <LoaderCircle size={18} /> : index + 1}</span>
      <span><strong>{label}</strong><small>{index < current ? "Done" : index > current ? "Next" : progress.phase === "QUEUED" ? "Waiting to start" : progress.phase === "OPENING" ? "Opening now" : "In progress"}</small></span>
    </li>)}
  </ol>;
}

export async function authenticateProductAccount({
  apiClient, login, password, businessSlug, allowLegacyFallback, legacyLogin, onLogin, onSelection, reload,
  onPreparing, signal, delay, now
}: {
  apiClient: Pick<ProductApi, "login" | "selectInstance" | "clearInstance" | "requestRuntimeInstance" | "subscription">;
  login: string;
  password: string;
  businessSlug?: string | undefined;
  allowLegacyFallback: boolean;
  legacyLogin: (login: string, password: string) => Promise<Session>;
  onLogin: (session: Session) => void;
  onSelection: (businesses: readonly ProductBusiness[]) => void;
  reload: () => void;
  onPreparing?: ((progress: WorkspacePreparation) => void) | undefined;
  signal?: AbortSignal | undefined;
  delay?: ((ms: number, signal?: AbortSignal) => Promise<void>) | undefined;
  now?: (() => number) | undefined;
}): Promise<void> {
  let result: Awaited<ReturnType<ProductApi["login"]>>;
  try {
    result = await apiClient.login(login, password, businessSlug);
  } catch (cause) {
    // WHY [BOL-PRODUCT-ROUTE-001]: only a failed product-auth attempt may
    // try the accepted legacy email login. A valid product login followed by
    // a routing failure must never silently enter the legacy workspace.
    if (!allowLegacyFallback || businessSlug || !isInvalidCredentials(cause)) throw cause;
    onLogin(await legacyLogin(login, password));
    return;
  }
  if (isBusinessSelection(result)) {
    onSelection(result.businesses);
    return;
  }
  const slug = result.business.slug;
  if (!slug || (businessSlug && slug !== businessSlug)) {
    throw new Error("The selected business did not match the signed-in account.");
  }
  // WHY [BOL-PRODUCT-ROUTE-002]: product auth is served by the platform API,
  // while workspace calls are routed by the server-owned instance cookie.
  // Do not fetch the old workspace or reload before the target is selected.
  if (slug === "agency-operations") {
    await apiClient.clearInstance();
    reload();
    return;
  }
  // WHY [BOL-SELF-SERVE-WEB-001]: a new business's own workspace may not
  // exist or run yet at its first sign-in. Only the server's typed
  // MISSING/NOT_RUNNING codes (or a non-RUNNING selection) enter the
  // "Preparing your workspace" wait; every other failure keeps the old
  // behavior (no reload, no legacy fallback). The page reloads only after the
  // server-owned selection cookie was set for a RUNNING workspace.
  let ready = false;
  try {
    const selected = await apiClient.selectInstance(slug);
    if (selected.instance.slug !== slug) throw new Error("The selected business did not match the signed-in account.");
    ready = selected.instance.state === "RUNNING";
  } catch (cause) {
    if (!isWorkspaceNotReady(cause)) throw cause;
  }
  if (!ready) {
    await prepareBusinessWorkspace({
      apiClient,
      slug,
      onProgress: onPreparing ?? (() => undefined),
      signal,
      ...(delay ? { delay } : {}),
      ...(now ? { now } : {})
    });
  }
  // A person who left this screen while a request was in flight must not be
  // reloaded into the business afterwards.
  throwIfPreparationAborted(signal);
  reload();
}

export function businessSlugFromName(name: string): string {
  const prefix = name.trim().toLowerCase().normalize("NFKD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .slice(0, 63);
  // A one-letter or non-Latin name must not create an invisible invalid field.
  // The server adds the workspace UUID, so this prefix is not the identity.
  return prefix.length >= 2 ? prefix : "business";
}

export function signupDeliveryMessage(adapter: "CONFIGURED" | "UNAVAILABLE"): string {
  // WHY [BOL-PRODUCT-SIGNUP-TRUTH-001]: signup deliberately returns the same
  // acceptance response for new and existing addresses. Copy must preserve
  // that anti-enumeration boundary instead of claiming an account was created.
  // Falsifier: the public response reveals whether an email already exists.
  return adapter === "CONFIGURED"
    ? "If this email can be registered, check its inbox for a six-digit code and a confirm button."
    : "The request was accepted, but email delivery is not configured. An administrator must provide a confirmation token for a new account.";
}

export function isBusinessSelection(value: ProductSession | { selectionRequired: true; businesses: readonly ProductBusiness[] }): value is { selectionRequired: true; businesses: readonly ProductBusiness[] } {
  return "selectionRequired" in value && value.selectionRequired === true;
}

function looksLikeEmail(value: string): boolean {
  return /^\S+@\S+\.\S+$/u.test(value.trim());
}

function isInvalidCredentials(value: unknown): boolean {
  return typeof value === "object" && value !== null
    && (("status" in value && value.status === 401) || ("code" in value && value.code === "INVALID_CREDENTIALS"));
}

function roleLabel(role: ProductBusiness["role"]): string {
  return role.charAt(0) + role.slice(1).toLowerCase();
}

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

const FALLBACK_TIME_ZONES = [
  "UTC", "Africa/Cairo", "America/Chicago", "America/Los_Angeles", "America/New_York",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Makassar", "Asia/Singapore", "Asia/Tokyo",
  "Australia/Sydney", "Europe/London", "Europe/Paris", "Pacific/Auckland"
];

function availableTimeZones(current: string): string[] {
  const intlWithTimeZoneList = Intl as typeof Intl & { supportedValuesOf?: (key: "timeZone") => string[] };
  let timeZones: string[];
  try {
    const supported = intlWithTimeZoneList.supportedValuesOf?.("timeZone");
    timeZones = supported?.length ? supported : FALLBACK_TIME_ZONES.filter(isValidTimeZone);
  } catch {
    timeZones = FALLBACK_TIME_ZONES.filter(isValidTimeZone);
  }

  const options = new Set(timeZones);
  for (const preserved of [current, "UTC"]) {
    if (isValidTimeZone(preserved)) options.add(preserved);
  }
  return [...options].sort((left, right) => left.localeCompare(right));
}

function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en", { timeZone });
    return true;
  } catch {
    return false;
  }
}

function timeZoneLabel(timeZone: string): string {
  const parts = timeZone.split("/");
  const name = (parts.pop() || timeZone).replace(/_/gu, " ");
  const region = parts.map((part) => part.replace(/_/gu, " ")).join(" / ");
  return region ? `${name} (${region})` : name;
}

function signupFieldMessage(field: keyof SignupDraft): string {
  return {
    displayName: "Enter your name (up to 160 characters).",
    email: "Enter a valid email address, such as name@company.com.",
    businessName: "Enter your business name (up to 160 characters).",
    timezone: "Choose a valid timezone.",
    password: "Use a password with 15–256 characters."
  }[field];
}
